import { Head, Link } from "@inertiajs/react"
import { AlertTriangle, BadgeCheck, CalendarClock } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"

type QueueItem = {
  content_type: string
  content_name: string
  context: string
  aspect: string
  verified_at: string | null
  source_name: string | null
  confidence: "high" | "medium" | "low" | null
  admin_path: string
}

export default function VerificationIndex({
  queue,
  summary,
}: {
  queue: QueueItem[]
  summary: { published_content: number; missing_verifications: number; tracked_aspects: number }
}) {
  const title = "Verification queue"
  const description = "Published content ordered by missing and oldest verification checks."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <header>
          <p className="public-kicker">Freshness operations</p>
          <h1 className="mt-2">{title}</h1>
          <p className="mt-2 max-w-2xl">Missing checks appear first, followed by the oldest checks. Open a record to add a verification in under 30 seconds.</p>
        </header>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <SummaryCard icon={BadgeCheck} label="Published content" value={summary.published_content} />
          <SummaryCard icon={AlertTriangle} label="Missing checks" value={summary.missing_verifications} signal />
          <SummaryCard icon={CalendarClock} label="Tracked aspects" value={summary.tracked_aspects} />
        </div>

        <div className="mt-7 overflow-hidden rounded-xl border border-hairline bg-surface">
          {queue.length > 0 ? queue.map((item, index) => (
            <Link
              key={`${item.admin_path}-${item.aspect}`}
              href={item.admin_path}
              className={`grid gap-2 px-4 py-4 text-ink-body no-underline hover:bg-surface-subtle sm:grid-cols-[minmax(0,1.5fr)_8rem_minmax(0,1fr)] sm:items-center sm:gap-4 ${index > 0 ? "border-t border-hairline" : ""}`}
            >
              <span className="min-w-0">
                <strong className="block truncate">{item.content_name}</strong>
                <span className="mt-0.5 block text-xs text-ink-muted">{item.content_type} · {item.context}</span>
              </span>
              <span className="text-sm font-semibold text-ink-secondary">{titleize(item.aspect)}</span>
              <span className="sm:text-right">
                {item.verified_at ? (
                  <span className="inline-flex flex-wrap items-center justify-end gap-2 text-sm text-ink-muted">
                    <span>{item.verified_at} · {item.source_name}</span>
                    <Badge tone={confidenceTone(item.confidence)}>{titleize(item.confidence ?? "")}</Badge>
                  </span>
                ) : (
                  <Badge tone="signal">Not yet verified</Badge>
                )}
              </span>
            </Link>
          )) : (
            <p className="p-6 text-center text-ink-muted">Publish content to begin the verification queue.</p>
          )}
        </div>
      </AdminShell>
    </>
  )
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  signal = false,
}: {
  icon: typeof BadgeCheck
  label: string
  value: number
  signal?: boolean
}) {
  return (
    <div className="rounded-xl border border-hairline bg-surface p-4">
      <Icon className={`h-5 w-5 ${signal ? "text-signal" : "text-accent"}`} aria-hidden />
      <strong className="mt-3 block text-2xl">{value}</strong>
      <span className="text-sm text-ink-muted">{label}</span>
    </div>
  )
}

function confidenceTone(confidence: QueueItem["confidence"]): "solid" | "accent" | "muted" {
  if (confidence === "high") return "solid"
  if (confidence === "medium") return "accent"
  return "muted"
}

function titleize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}
